import React, { useContext, useEffect, useState } from "react";
import { Card, Form, Input, Button, Select, InputNumber, Popconfirm, notification, List, Collapse, Typography, Switch, Flex } from 'antd'
import { BgColorsOutlined, BulbOutlined } from '@ant-design/icons';
import { useGetUserDetailsQuery, useUpdateUserDetailsMutation, useGetUserNeighboursQuery } from '../../services/auth/authService'
import { ThemeModeContext } from '../../App';
const { Option } = Select;
const { Text } = Typography;

const EditSubComponent = () => {
    const [form] = Form.useForm();
    const [api, contextHolder] = notification.useNotification();
    const [selectedAddress, setSelectedAddress] = useState('');
    const { themeMode, setThemeMode } = useContext(ThemeModeContext);
    const { data: userInfo = {}, isFetching: isUserInfoFetching } = useGetUserDetailsQuery()
    const [updateInfo, { isLoading: isUpdateInfoLoading }] = useUpdateUserDetailsMutation()
    const { refetch } = useGetUserNeighboursQuery()
    useEffect(() => {
        form.resetFields()
        setSelectedAddress(userInfo.address)
    }, [userInfo, form])
    const openSuccessNotification = (mes) => {
        api.success({
            message: 'Повідомлення',
            description: mes ?? 'Ваші зміни успішно збережено'
        });
    };
    const openErrorNotification = (mes) => {
        api.error({
            message: 'Повідомлення',
            description: mes
        });
    };
    const showPopconfirm = () => {
        if (userInfo.bills?.some(i => !i.paid)) {
            openErrorNotification('Спочатку оплатіть неоплачені рахунки')
            return
        }
        if (userInfo.blocked) {
            updateInfo({
                phone: userInfo.phone,
                address: userInfo.address,
                flat: userInfo.flat,
                keys_count: userInfo.keys_count,
                blocked: !userInfo.blocked
            }).unwrap().then(() => {
                openSuccessNotification('Успішно розблоковано')
                refetch()
            })
        }
    };
    const handleOkOfConfirmation = () => {
        updateInfo({
            phone: userInfo.phone,
            address: userInfo.address,
            flat: userInfo.flat,
            keys_count: userInfo.keys_count,
            blocked: !userInfo.blocked,
        }).unwrap().then(() => {
            openSuccessNotification('Успішно заблоковано')
            refetch()
        })
    };
    const onFormSave = (values) => {
        Object.keys(values).forEach(key => values[key] === undefined && delete values[key]) // remove undefined
        updateInfo({
            phone: userInfo.phone,
            address: userInfo.address,
            flat: userInfo.flat,
            keys_count: userInfo.keys_count,
            ...values
        }).unwrap().then(() => {
            openSuccessNotification('Дані успішно збережені')
            refetch()
        }).catch(rejected => {
            openErrorNotification(Object.values(rejected?.data).join(''))
        })
    }
    const onUnhideNeighbour = (phone) => {
        const currentHidden = userInfo.hidden_neighbours || [];
        const currentPayingFor = userInfo.paying_for || [];
        const nextHidden = currentHidden.filter((p) => p !== phone);
        const nextPayingFor = currentPayingFor.filter((p) => p !== phone);
        updateInfo({
            phone: userInfo.phone,
            address: userInfo.address,
            flat: userInfo.flat,
            keys_count: userInfo.keys_count,
            hidden_neighbours: nextHidden,
            paying_for: nextPayingFor,
        }).unwrap().then(() => {
            openSuccessNotification('Сусіда знову показано')
            refetch()
        }).catch(rejected => {
            openErrorNotification(Object.values(rejected?.data).join(''))
        })
    }
    const flatNumbers = () => {
        const flats = [...Array(170).keys()].map((i) => i + 1);
        flats.splice(162, 0, "162-a")
        flats.splice(167 + 1, 0, "167-1", "167-2", "167-3", "167-4")
        flats.splice(170 + 5, 0, "170-1", "170-2", "170-3", "170-4")
        return flats.map((i) => <Option key={i} value={i}>{i}</Option>)
    }
    const hiddenNeighboursInfo = (userInfo.hidden_neighbours_info && userInfo.hidden_neighbours_info.length > 0)
        ? userInfo.hidden_neighbours_info
        : (userInfo.hidden_neighbours || []).map((phone) => ({
            phone,
            first_name: '',
            last_name: '',
        }));
    return (
        <Card className="compact-section-card compact-form-card" bordered={false}>
            {contextHolder}
            <div className="compact-section-head">
                <Text className="compact-section-title">Редагування акаунту</Text>
                <Text className="compact-section-subtitle">Оновлюйте дані та керуйте видимістю сусідів</Text>
            </div>
            <Card className="settings-theme-card" bordered={false}>
                <Flex justify="space-between" align="center" gap={16} wrap="wrap">
                    <div>
                        <Text className="compact-section-title">Тема інтерфейсу</Text>
                        <div>
                            <Text className="compact-section-subtitle">Темний режим зберігається для цього пристрою</Text>
                        </div>
                    </div>
                    <Switch
                        checked={themeMode === 'dark'}
                        onChange={(checked) => setThemeMode(checked ? 'dark' : 'light')}
                        checkedChildren={<BulbOutlined />}
                        unCheckedChildren={<BgColorsOutlined />}
                    />
                </Flex>
            </Card>
            <Form
                form={form}
                name="edit"
                initialValues={{
                    first_name: userInfo.first_name,
                    last_name: userInfo.last_name,
                    phone: userInfo.phone,
                    address: userInfo.address,
                    flat: userInfo.flat,
                    keys_count: userInfo.keys_count,
                    firm_name: userInfo.firm_name
                }}
                onFinish={onFormSave}
                layout="vertical"
                size="middle"
                className='edit-form'
            >
                <Form.Item
                    label="Ім'я"
                    name="first_name"
                    rules={[
                        {
                            required: true,
                            message: 'Будь ласка вкажіть ваше ім\'я',
                        },
                        {
                            min: 3,
                            message: 'Мінімальна довжина має бути 3 символи',
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Прізвище"
                    name="last_name"
                    rules={[
                        {
                            required: true,
                            message: 'Будь ласка вкажіть ваше прізвище',
                        },
                        {
                            min: 3,
                            message: 'Мінімальна довжина має бути 3 символи',
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Номер телефону"
                    name="phone"
                    rules={[
                        {
                            required: true,
                            message: 'Будь ласка вкажіть номер телефону',
                        },
                        {
                            pattern: '^\\+380[0-9]{9}$',
                            message: 'Вкажіть правильний номер телефону'
                        }
                    ]}
                >
                    <Input disabled type="tel" />
                </Form.Item>
                <Form.Item
                    label="Адреса"
                    name="address"
                    rules={[
                        {
                            required: true,
                            message: 'Будь ласка вкажіть адресу',
                        },
                    ]}
                >
                    <Select onChange={(val) => setSelectedAddress(val)}>
                        <Option key={1} value="Вадима Гетьмана 1А">
                            Вадима Гетьмана 1А
                        </Option>
                        <Option key={2} value="Вадима Гетьмана 1Б">Вадима Гетьмана 1Б</Option>
                        <Option key={3} value="Вадима Гетьмана 1В">Вадима Гетьмана 1В</Option>
                        <Option key={4} value="Вадима Гетьмана 1">Вадима Гетьмана 1</Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    label="Квартира"
                    name="flat"
                    rules={[
                        {
                            required: true,
                            message: 'Будь ласка вкажіть квартиру',
                        },
                    ]}
                >
                    <Select showSearch>
                        {flatNumbers()}
                    </Select>
                </Form.Item>
                {selectedAddress === "Вадима Гетьмана 1" &&
                    <div>
                        <Form.Item
                            label="Кількість ключів"
                            name="keys_count"
                            rules={[
                                {
                                    required: true,
                                    message: 'Будь ласка вкажіть кількість ключів',
                                },
                            ]}
                        >
                            <InputNumber min={1} controls={false} />
                        </Form.Item>
                        <Form.Item
                            label="Назва фірми"
                            name="firm_name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Будь ласка вкажіть назву фірми',
                                }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </div>
                }
                <Form.Item
                    label="Пароль"
                    name="password"
                    tooltip='Якщо залишити пустим пароль не зміниться'
                    rules={[
                        {
                            min: 6,
                            message: 'Мінімальна довжина паролю має бути 6 символів',
                        },
                    ]}
                >
                    <Input.Password autoComplete="new-password" />
                </Form.Item>
                {hiddenNeighboursInfo.length > 0 && (
                    <Collapse
                        defaultActiveKey={[]}
                        style={{ marginBottom: 16 }}
                        items={[
                            {
                                key: 'hidden-neighbours',
                                label: 'Приховані сусіди',
                                children: (
                                    <List
                                        bordered
                                        size="small"
                                        dataSource={hiddenNeighboursInfo}
                                        renderItem={(neighbour) => (
                                            <List.Item
                                                actions={[
                                                    <Button key={`unhide-${neighbour.phone}`} type="link" onClick={() => onUnhideNeighbour(neighbour.phone)}>
                                                        Показати
                                                    </Button>,
                                                ]}
                                            >
                                                {`${[neighbour.first_name, neighbour.last_name].filter(Boolean).join(' ') || 'Користувач'} (${neighbour.phone})`}
                                            </List.Item>
                                        )}
                                    />
                                ),
                            },
                        ]}
                    />
                )}

                <Form.Item>
                    <Button loading={isUpdateInfoLoading} block type="primary" htmlType="submit">
                        Зберегти
                    </Button>
                </Form.Item>
                <Form.Item>
                    <Popconfirm
                        title="Підтвердження"
                        description="Ви впевнені, що хочете заблокувати акаунт?"
                        okText="Так"
                        cancelText="Ні"
                        onConfirm={handleOkOfConfirmation}
                        okButtonProps={{
                            loading: isUpdateInfoLoading,
                        }}
                        disabled={userInfo.blocked}
                    >
                        <Button loading={isUpdateInfoLoading || isUserInfoFetching} onClick={showPopconfirm} block danger={!userInfo.blocked} type="primary">
                            {userInfo.blocked ? 'Розблокувати' : 'Заблокувати'} акаунт
                        </Button>
                    </Popconfirm>
                </Form.Item>
            </Form>
        </Card>
    )
}

export default EditSubComponent
